<script setup lang="ts">
import { useNavigationSearch } from "#imports";

const isGoogleInspect = useState('isGoogleInspect', () => false)

const route = useRoute()
const requestUrl = useRequestURL()
const { resolvePath } = useNavigationSearch()
const { getAvailableLanguages } = useInternationalization();
const { loadNavigationElements } = useNavigation()
const { loadNavigationElements: loadFooterNavigationElements } = useNavigation({
  type: 'footer-navigation',
})

loadNavigationElements({ depth: 3 })
loadFooterNavigationElements({ depth: 1 })

const { data: seoResult } = await useAsyncData(`seoResult-${route.path}`, async () => {
  const response = await resolvePath(route.path)

  return response || []
})

watchEffect(() => {
  useHead({
    title: "Kavos Bankas",
    meta: [{ name: "description", content: "Kavos Bankas – didžiausias skrudintojas Lietuvoje! Šviežiai skrudinta kava Jūsų namams ir biurui." }],
    link: [
      {
        rel: 'canonical',
        href: getCanonicalUrl(seoResult) ? `${requestUrl.origin}/${getCanonicalUrl(seoResult)}` : ''
      },
    ],
    htmlAttrs: {
      lang: "lt",
    },
    script: [
      !isGoogleInspect.value && useRuntimeConfig()?.public?.scripts?.userCentrics?.id ? {
        id: 'usercentrics-cmp',
        src: 'https://web.cmp.usercentrics.eu/ui/loader.js',
        'data-settings-id': useRuntimeConfig()?.public.scripts.userCentrics.id,
        async: true,
      } : undefined
    ].filter((script) => script !== undefined)
  });
})

const { apiClient } = useShopwareContext();
// If you enable runtimeConfig.shopware.useUserContextInSSR, then you can use this code to share session between server and client.
const { data: sessionContextData, status } = await useAsyncData(
  "sessionContext",
  async () => {
    const response = await apiClient.invoke("readContext get /context", {
      headers: {
        "sw-context-token": useCookie('sw-context-token').value,
      },
    });

    return response?.data
  }
);

const customFields = computed(() => sessionContextData.value?.salesChannel?.customFields)

const { defineOrganization } = useSchemaOrgJson()
defineOrganization((customFields?.value?.phoneNumber as string) || '')

// read the locale/lang code from accept-language header (i.e. en, en-GB or de-DE)
// and set configuration for price formatting globally
const headers = useRequestHeaders();
// Extract the first (with highest priority order) locale or lang code from accept-language header
// for example: "en-US;q=0.7,en;q=0.3" will return "en-US"
const localeFromHeader = headers?.["accept-language"]
  ?.split(",")
  ?.map(
    (languageConfig) => languageConfig.match(/^([a-z]{2}(?:-[A-Z]{2})?)/)?.[0]
  )
  .find(Boolean);

usePrice({
  currencyCode: sessionContextData.value?.currency?.isoCode || "",
  localeCode: localeFromHeader,
});

useSessionContext(sessionContextData.value);

const { getWishlistProducts } = useWishlist();
const { refreshCart } = useCart();

if (import.meta.client) {
  // getting the wishlist products should not block SSR
  if ((useRouter().currentRoute.value.name as string).includes("wishlist")) {
    getWishlistProducts();
  }
}

onMounted(() => {
  getAvailableLanguages()
  useAddress()

  setTimeout(() => {
    !isGoogleInspect.value && refreshCart()
  }, 500)
})
</script>

<template>
  <div>
    <NuxtLoadingIndicator />

    <NuxtLayout>
      <ClientOnly>
        <Toast style="top: 16px; right: 16px" class="max-sm:w-72" group="add-to-cart" :pt="{
          messageContent: 'relative',
          buttonContainer: 'absolute right-0 pr-2',
        }">
          <template #message="slotProps">
            <div class="flex flex-col items-start flex-auto gap-y-3">
              <div class="font-medium pr-4">
                {{ slotProps?.message?.summary }}
              </div>

              <Message v-if="slotProps?.message?.detail" severity="secondary" class="px-2" size="small"
                icon="pi pi-info-circle">
                {{ slotProps?.message?.detail }}
              </Message>

              <NuxtLink to="/checkout" class="self-end">
                <Button size="small" :label="$t('cart.checkout')" severity="success" />
              </NuxtLink>
            </div>
          </template>
        </Toast>
      </ClientOnly>

      <ClientOnly>
        <Toast style="top: 16px; right: 16px" class="max-sm:w-72" />
      </ClientOnly>

      <NuxtPage />
    </NuxtLayout>
  </div>
</template>

<style>
@import url("https://fonts.googleapis.com/css2?family=Sen:wght@400..800&display=swap");

*::-webkit-scrollbar {
  display: none;
}

* {
  -ms-overflow-style: none;
  /* IE and Edge */
  scrollbar-width: none;
  /* Firefox */
}
</style>
